import { ApolloCache, useApolloClient } from '@apollo/client';
import {
  ArticleFullFieldsFragment,
  ArticleFullFieldsFragmentDoc,
  ArticleUpdateMutation,
  ArticleUpdateMutationVariables,
  Tag,
  useArticleUpdateMutation,
} from 'lib-knowledge-management-common/graphql';

type MutationFunction = ReturnType<typeof useArticleUpdateMutation>[0];
type MutationReturnValue = ReturnType<MutationFunction>;

export type UpdateFn = (params: {
  articleId: string;
  tagsToAdd: Array<Tag>;
  tagsToRemove: Array<Tag>;
}) => MutationReturnValue;

type UseDiscardChangesReturnValue = [
  UpdateFn,
  ReturnType<typeof useArticleUpdateMutation>[1],
];

/**
 * Update the tags for the article.
 *
 * @param articleId
 */
function useArticleTagsUpdate(): UseDiscardChangesReturnValue {
  const [updateFn, mutationResult] = useArticleUpdateMutation();
  const { cache } = useApolloClient();

  const update: UpdateFn = async ({ articleId, tagsToAdd, tagsToRemove }) => {
    const optimisticResponse = createOptimisticResponse({
      cache,
      articleId,
      tagsToAdd,
      tagsToRemove,
    });

    const tags: ArticleUpdateMutationVariables['tags'] = {};
    if (tagsToAdd.length) {
      tags.add = tagsToAdd.map((t) => t.id);
    }
    if (tagsToRemove.length) {
      tags.remove = tagsToRemove.map((t) => t.id);
    }

    const value = await updateFn({
      variables: {
        articleId,
        tags,
      },
      optimisticResponse,
    });

    return value;
  };

  return [update, mutationResult];
}

export default useArticleTagsUpdate;

function createOptimisticResponse({
  cache,
  articleId,
  tagsToAdd,
  tagsToRemove,
}: {
  cache: ApolloCache<object>;
  articleId: string;
  tagsToAdd: Array<Tag>;
  tagsToRemove: Array<Tag>;
}): ArticleUpdateMutation | undefined {
  const article = cache.readFragment<ArticleFullFieldsFragment>({
    fragment: ArticleFullFieldsFragmentDoc,
    id: cache.identify({
      __typename: 'Article',
      id: articleId,
    }),
  });

  if (!article) throw new Error('Missing article in useArticleUpdate');

  type GraphQLTag = ArticleFullFieldsFragment['tags'][number];

  const tags = article.tags.reduce(
    (acc, tag) => {
      acc[tag.id] = tag;
      return acc;
    },
    {} as { [id: string]: GraphQLTag },
  );

  tagsToAdd.forEach(
    (t) => (tags[t.id] = { ...t, color: t.color ?? null, __typename: 'Tag' }),
  );
  tagsToRemove.forEach((t) => delete tags[t.id]);

  return {
    __typename: 'Mutations',
    articleUpdate: {
      ...article,
      // sort tags, as that is what the BE does.
      tags: Object.values(tags).sort((a, b) => {
        if (a.name > b.name) return 1;
        if (a.name < b.name) return -1;
        return 0;
      }),
    },
  };
}
